import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Box, CircularProgress, Typography } from '@mui/material';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

/**
 * Componente que protege rotas verificando:
 * 1. Se o usuário está autenticado
 * 2. Se o usuário tem assinatura ativa
 * 
 * Redireciona para /tw/pagamento se não tiver assinatura ativa
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { signed, user, subscription, loading: authLoading } = useAuth();
  const location = useLocation();
  const [checking, setChecking] = useState(true);

  // Rotas que não requerem assinatura (podem acessar mesmo sem pagar)
  const PUBLIC_AFTER_LOGIN_ROUTES = [
    '/tw/pagamento',
    '/tw/pagamento/sucesso',
    '/tw/profile', // Permitir editar perfil
    '/tw/selecao-empresa', // Seleção de empresa multi-tenant
  ];

  useEffect(() => {
    // Aguardar carregamento do contexto de autenticação
    if (!authLoading) {
      setChecking(false);
    }
  }, [authLoading]);

  // Mostra loading enquanto verifica autenticação
  if (authLoading || checking) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Verificando acesso...
        </Typography>
      </Box>
    );
  }

  // Se não está autenticado, redireciona para login
  if (!signed) {
    return <Navigate to="/tw/login" state={{ from: location }} replace />;
  }

  // Se está em rota pública (após login), permite acesso
  if (PUBLIC_AFTER_LOGIN_ROUTES.includes(location.pathname)) {
    return <>{children}</>;
  }

  // PRIORIDADE 1: Verificar se tem acesso gratuito liberado (needsPayment === false)
  // Este campo vem do backend quando acessoGratisLiberado = true na tabela usuario
  const hasAcessoGratisLiberado = user?.needsPayment === false;

  // Se tem acesso grátis liberado, permite acesso SEMPRE
  if (hasAcessoGratisLiberado) {
    return <>{children}</>;
  }
  
  // TAMBÉM verificar no objeto subscription (backup)
  if (subscription?.needsPayment === false) {
    return <>{children}</>;
  }

  // PRIORIDADE 2: Verificar se tem assinatura ativa (somente para quem precisa pagar)
  // Backend retorna status: ATIVA, TRIAL, CANCELADA, PENDENTE, INACTIVE
  const hasActiveSubscription = subscription && (
    subscription.status === 'ATIVA' ||
    subscription.status === 'TRIAL' ||
    subscription.status === 'FREE_ACCESS' || // Acesso gratuito
    subscription.hasActiveAccess === true
  );

  // Se não tem assinatura ativa e não tem acesso grátis, redireciona para pagamento
  if (!hasActiveSubscription) {
    return <Navigate to="/tw/pagamento" state={{ from: location }} replace />;
  }

  // Se está tudo OK, renderiza o conteúdo
  return <>{children}</>;
};

export default ProtectedRoute;
